import { MatterSubItemContextMenu } from "@/components/features/matter/context-menus/MatterSubItemContextMenu";
import { useUser } from "@/components/UserProvider";
import { useDeleteSignatureRequest } from "@/lib/data/react-query/matters/matter-signature-request";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterSignatureRequest } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import type { ReactNode } from "react";

interface MatterSignatureRequestContextMenuProps {
  enabled: boolean;
  matter: Matter;
  onEdit: () => void;
  signatureRequest: MatterSignatureRequest;
  trigger: ReactNode;
}

export const MatterSignatureRequestContextMenu = ({
  enabled,
  matter,
  onEdit,
  signatureRequest,
  trigger,
}: MatterSignatureRequestContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const deleteSignatureRequest = useDeleteSignatureRequest();
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSignatureRequest,
    ACTION_PERMISSION_VALUES.delete,
  );

  if (!enabled) {
    return <>{trigger}</>;
  }

  return (
    <MatterSubItemContextMenu
      copyValue={[
        signatureRequest.matterFileName,
        signatureRequest.matterSignatureRequestExpirationDate
          ? `Expires: ${formatDate(signatureRequest.matterSignatureRequestExpirationDate)}`
          : undefined,
      ]
        .filter(Boolean)
        .join("\n")}
      delete={
        canDelete
          ? {
              disabled: deleteSignatureRequest.isPending,
              onSelect: async () => {
                await deleteSignatureRequest.mutateAsync({
                  signatureRequestId: signatureRequest.matterSignatureRequestId,
                });
              },
            }
          : false
      }
      entityLabel="signature request"
      entityName="signature request"
      matter={matter}
      onEdit={onEdit}
      subItem={{
        id: signatureRequest.matterSignatureRequestId,
        type: "signature-request",
      }}
      trigger={trigger}
    />
  );
};
